import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { DEFAULT_SITE_IMAGES, SiteImageKey, SiteImageMap } from '../assets/siteImages';

interface SiteImagesContextValue {
  images: SiteImageMap;
  databaseEngine: 'mysql' | 'disk-database' | null;
  isLoading: boolean;
  loadError: string | null;
  refreshImages: () => Promise<void>;
  saveImage: (key: SiteImageKey, imageUrl: string) => Promise<void>;
}

const SiteImagesContext = createContext<SiteImagesContextValue | undefined>(undefined);

export const SiteImagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [images, setImages] = useState<SiteImageMap>(DEFAULT_SITE_IMAGES);
  const [databaseEngine, setDatabaseEngine] = useState<'mysql' | 'disk-database' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const refreshImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/site-images');
      const result = await response.json();
      if (!response.ok || !result.success || !result.images || typeof result.images !== 'object') {
        throw new Error(result.error || 'Could not load site images from the database.');
      }

      setImages({ ...DEFAULT_SITE_IMAGES, ...result.images });
      setDatabaseEngine(result.database === 'mysql' ? 'mysql' : 'disk-database');
      setLoadError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not load site images from the database.';
      setLoadError(message);
      console.error('Failed to load site images:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshImages();
  }, [refreshImages]);

  const saveImage = useCallback(async (key: SiteImageKey, imageUrl: string) => {
    const response = await fetch(`/api/site-images/${encodeURIComponent(key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageUrl })
    });
    const result = await response.json();
    if (!response.ok || !result.success || result.image?.key !== key || result.image?.imageUrl !== imageUrl) {
      throw new Error(result.error || 'The image could not be saved to the database.');
    }

    setImages((current) => ({ ...current, [key]: result.image.imageUrl }));
    setLoadError(null);
  }, []);

  return (
    <SiteImagesContext.Provider value={{ images, databaseEngine, isLoading, loadError, refreshImages, saveImage }}>
      {children}
    </SiteImagesContext.Provider>
  );
};

export function useSiteImages(): SiteImagesContextValue {
  const context = useContext(SiteImagesContext);
  if (!context) {
    throw new Error('useSiteImages must be used inside SiteImagesProvider');
  }
  return context;
}
